import { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const DriverForm = () => {
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);
    const [resettingPassword, setResettingPassword] = useState(false);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [phoneNo, setPhoneNo] = useState("");
    const [isAvailable, setIsAvailable] = useState(true);

    const [newPassword, setNewPassword] = useState("");

    useEffect(() => {
        if (!isEditMode) {
            setLoading(false);
            return;
        }
        const fetchDriver = async () => {
            try {
                const { data } = await axios.get('/api/v1/admin/drivers');
                const driver = data.drivers.find((d) => d._id === id);
                if (!driver) {
                    enqueueSnackbar("Driver not found", { variant: "error" });
                    navigate('/admin/drivers');
                    return;
                }
                setName(driver.name);
                setEmail(driver.email);
                setPhoneNo(driver.phoneNo || "");
                setIsAvailable(driver.isAvailable);
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message || "Failed to load driver", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchDriver();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const submitHandler = async (e) => {
        e.preventDefault();
        if (!name.trim()) return enqueueSnackbar("Please enter a name", { variant: "warning" });

        setSaving(true);
        try {
            if (isEditMode) {
                await axios.put(`/api/v1/admin/driver/${id}`, { name, phoneNo, isAvailable });
                enqueueSnackbar("Driver updated", { variant: "success" });
            } else {
                if (!email.trim() || password.length < 8) {
                    enqueueSnackbar("Please enter an email and a password of at least 8 characters", { variant: "warning" });
                    setSaving(false);
                    return;
                }
                await axios.post('/api/v1/admin/driver/new', { name, email, password, phoneNo });
                enqueueSnackbar("Driver created", { variant: "success" });
            }
            navigate('/admin/drivers');
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save driver", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    const resetPasswordHandler = async () => {
        if (newPassword.length < 8) {
            enqueueSnackbar("New password must be at least 8 characters", { variant: "warning" });
            return;
        }
        setResettingPassword(true);
        try {
            await axios.put(`/api/v1/admin/driver/${id}/reset-password`, { newPassword });
            enqueueSnackbar("Password reset successfully", { variant: "success" });
            setNewPassword("");
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to reset password", { variant: "error" });
        } finally {
            setResettingPassword(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title={`Admin: ${isEditMode ? "Edit" : "New"} Driver | 1313 Mart`} />
            {saving && <BackdropLoader />}

            <form onSubmit={submitHandler} className="flex flex-col gap-4 bg-white rounded-lg shadow p-4 sm:p-6" id="driverform">

                <h1 className="text-lg font-medium uppercase">{isEditMode ? "Edit" : "New"} Driver</h1>

                <TextField label="Name" variant="outlined" size="small" required fullWidth value={name} onChange={(e) => setName(e.target.value)} />

                {!isEditMode && (
                    <div className="flex flex-col sm:flex-row gap-4">
                        <TextField label="Email (used to log in)" type="email" variant="outlined" size="small" required fullWidth value={email} onChange={(e) => setEmail(e.target.value)} />
                        <TextField label="Password" type="password" variant="outlined" size="small" required fullWidth value={password} onChange={(e) => setPassword(e.target.value)} helperText="At least 8 characters" />
                    </div>
                )}

                <TextField label="Phone Number" variant="outlined" size="small" fullWidth value={phoneNo} onChange={(e) => setPhoneNo(e.target.value)} />

                {isEditMode && (
                    <FormControlLabel
                        control={<Checkbox checked={isAvailable} onChange={(e) => setIsAvailable(e.target.checked)} />}
                        label="Available for new deliveries"
                    />
                )}

                <div className="flex justify-end">
                    <input form="driverform" type="submit" className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg cursor-pointer" value={isEditMode ? "Save Changes" : "Create Driver"} />
                </div>

            </form>

            {isEditMode && (
                <div className="flex flex-col gap-3 bg-white rounded-lg shadow p-4 sm:p-6 mt-4">
                    <h2 className="text-lg font-medium uppercase">Reset Password</h2>
                    <p className="text-sm text-gray-500">Drivers can't use "forgot password" themselves — set a new one directly here and share it with them.</p>
                    <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                        <TextField
                            label="New Password"
                            type="password"
                            variant="outlined"
                            size="small"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            helperText="At least 8 characters"
                            sx={{ minWidth: 260 }}
                        />
                        <button
                            onClick={resetPasswordHandler}
                            disabled={resettingPassword}
                            className="bg-primary-blue text-white text-sm font-medium px-4 py-2 rounded shadow hover:shadow-lg disabled:opacity-50"
                        >
                            Reset Password
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default DriverForm;
